import React, { Component } from "react";
import { Link } from "react-router-dom";
import client from "@culturehq/client";
import { Pagination, PlainButton } from "@culturehq/components";

import { useProfile } from "app/context";
import { canCreateEvent } from "app/permissions";
import { errorService, seo, t, t as translate, Trans } from "app/utils";

import ErrorBoundary from "components/common/ErrorBoundary";
import ErrorMessage from "components/common/ErrorMessage";
import EventCard from "components/common/EventCard";
import Placeholder from "components/common/event-card/Placeholder";
import { QueryStringProvider, QueryStringConsumer } from "app/context/QueryStringContext";

import EventFilters from "./events/EventFilters";
import EventPhotos from "./events/EventPhotos";
import SuggestedEvents from "./events/SuggestedEvents";

const sponsoredOptions = ["t", "f"];
const userRelationOptions = ["host", "responded", "invited"];
const whenOptions = {
  past: "-ends_at",
  present: "+ends_at",
  future: "+starts_at"
};

const queryToOptions = queryString => {
  const { d, i, l, m, s, t } = queryString;
  const hasWhen = Object.prototype.hasOwnProperty.call(whenOptions, d);

  return {
    allLocations: !l,
    eventTypes: t || null,
    interestIds: i || null,
    locationIds: l || null,
    sponsored: sponsoredOptions.includes(s) ? (s === "t") : null,
    userRelation: userRelationOptions.includes(m) ? m : null,
    when: hasWhen ? d : null,
    sort: hasWhen ? whenOptions[d] : "-starts_at"
  };
};

const queryStringParsers = {
  d: "string",
  i: "intList",
  l: "intList",
  m: "string",
  s: "string",
  t: "stringList"
};

class EventsContext extends Component {
  constructor(props) {
    super(props);

    this.state = {
      page: 1,
      options: queryToOptions(props.queryString),
      events: [],
      pagination: {},
      loading: true
    };
  }

  componentDidMount() {
    this.fetchEvents();
    seo({
      title: translate("Events - CultureHQ.com")
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const { queryString } = this.props;
    const { options, page } = this.state;

    if (options !== prevState.options || page !== prevState.page) {
      this.fetchEvents();
    }

    if (queryString !== prevProps.queryString) {
      this.setState(current => ({
        ...current,
        options: { ...current.options, ...queryToOptions(queryString) },
        page: 1
      }));
    }
  }

  handleEventUpdate = event => {
    this.setState(({ events }) => ({
      events: events.map(prevEvent => (
        prevEvent.id === event.id ? { ...prevEvent, ...event } : prevEvent
      ))
    }));
  };

  handleEventDelete = eventId => {
    this.setState(({ events }) => ({
      events: events.filter(prevEvent => (
        prevEvent.id !== eventId
      ))
    }));
  };

  handlePageSelected = page => {
    this.setState({ page });
  };

  async fetchEvents() {
    const { options, page } = this.state;
    this.setState({ loading: true });

    try {
      const { events, pagination } = await client.listEvents({ ...options, page });
      this.setState({ events, pagination, loading: false });
    } catch (error) {
      errorService.error(error);
      this.setState({ error, loading: false });
    }
  }

  render() {
    const { onClearQueryString, profile } = this.props;
    const { error, events, loading, options, pagination } = this.state;

    return (
      <div className="Events clearfix">
        <section role="complementary" aria-label={t("Left bar - Filters")}>
          <EventFilters options={options} loading={loading} pagination={pagination} />
        </section>
        <section role="main">
          <div className="Central--column col-xs-12 col-sm-8 col-md-9 col-lg-7">
            <ErrorBoundary>
              {error && <ErrorMessage error={error} />}

              {loading ? (
                <>{[1, 2, 3].map(index => <Placeholder key={index} />)}</>
              ) : (
                <>
                  {events.map(event => (
                    <EventCard
                      key={event.id}
                      event={event}
                      inList
                      onEventUpdate={this.handleEventUpdate}
                      onEventDelete={this.handleEventDelete}
                    />
                  ))}
                  {events.length < 1 && (
                    <div className="EmptyResults EmptyResults-events">
                      <h2>
                        <Trans>Oh no, you ran out of results...</Trans>
                      </h2>
                      {canCreateEvent(profile) && (
                        <>
                          <h3>
                            <Trans>What would you like to do next?</Trans>
                          </h3>
                          <Link className="button" to="/events/new/type">
                            <Trans>Create Event</Trans>
                          </Link>
                          <br />
                        </>
                      )}
                      <PlainButton onClick={onClearQueryString}>
                        <Trans>Start over</Trans>
                      </PlainButton>
                    </div>
                  )}
                </>
              )}

              <Pagination {...pagination} onClick={this.handlePageSelected} />
            </ErrorBoundary>
          </div>
        </section>
        <section role="complementary" aria-label={t("Right bar")}>
          <div className="right-column hidden-xs col-sm-4 col-md-3 col-lg-3">
            <EventPhotos />
            <SuggestedEvents />
          </div>
        </section>
      </div>
    );
  }
}

const Events = () => {
  const { profile } = useProfile();

  return (
    <QueryStringProvider parsers={queryStringParsers}>
      <QueryStringConsumer>
        {queryStringContext => (
          <EventsContext {...queryStringContext} profile={profile} />
        )}
      </QueryStringConsumer>
    </QueryStringProvider>
  );
};

export default Events;
